import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Sellerimg from "../img/Sellerimg.svg";
import api from "../../api/axiosInstance";

const ChangePassword = () => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const [errors, setErrors] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const getFirstPasswordError = (password) => {
    if (password.length < 8) {
      return "Your password must contain minimum of 8 characters.";
    }
    if (!/[A-Z]/.test(password)) {
      return "Your password must contain at least one uppercase letter.";
    }
    if (!/[a-z]/.test(password)) {
      return "Your password must contain at least one lowercase letter.";
    }
    if (!/[!@#$%^&*(),.?\":{}|<>]/.test(password)) {
      return "Your password must contain at least one special character. (e.g., @, #, $, etc.)";
    }
    return "";
  };

  const handleChangePassword = async () => {
    const newErrors = {
      currentPassword: currentPassword ? "" : "Enter your current password",
      newPassword: newPassword ? "" : "Enter your new password.",
      confirmNewPassword: confirmNewPassword
        ? ""
        : "Confirm your new password.",
    };

    if (!currentPassword) {
      newErrors.currentPassword = "Enter your current password";
    }

    if (!newPassword) {
      newErrors.newPassword = "Enter your new password.";
    } else {
      const firstRuleError = getFirstPasswordError(newPassword);
      if (firstRuleError) {
        newErrors.newPassword = firstRuleError;
      }
    }

    if (!confirmNewPassword) {
      newErrors.confirmNewPassword = "Confirm your new password.";
    } else if (newPassword && newPassword !== confirmNewPassword) {
      newErrors.confirmNewPassword =
        "Entered confirm password does not match the new password.";
    }

    const hasErrors = Object.values(newErrors).some((err) => err !== "");
    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    setErrors({ currentPassword: "", newPassword: "", confirmNewPassword: "" });

    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      showModal("Please log in first.");
      return;
    }

    try {
      const response = await api.post(
        "/api/v1/change-password",
        {
          current_password: currentPassword,
          new_password1: newPassword,
          new_password2: confirmNewPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.status === 200 && response.data.status === "success") {
        showModal("Password changed successfully.");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("user_id");
        setTimeout(() => navigate("/"), 1500);
      } else {
        showModal(response.data.message || "Failed to change password.");
      }
    } catch (error) {
      console.error("Password change error:", error);
      showModal(
        error.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    }
  };

  const showModal = (message) => {
    setModalMessage(message);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalMessage("");
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row px-5">
      {/* Left Content */}
      <div
        className="w-full lg:w-1/2 min-h-[300px] lg:min-h-screen bg-no-repeat bg-center bg-cover"
        style={{
          backgroundImage: `url(${Sellerimg})`,
          backgroundSize: "contain",
        }}
      ></div>

      {/* Right Content (Background Image) */}
      <div className="w-full lg:w-1/2 flex justify-center mt-4 p-6">
        <div className="w-full sm:w-3/4 h-auto flex flex-col justify-center p-6 rounded-md bg-white">
          <h1 className="font-inter text-3xl font-semibold mb-[30px] text-center">
            Change Password
          </h1>

          {/* Current Password */}
          <div className="mb-4">
            <label className="block text-sm mb-1 font-normal font-inter">
              Current Password<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-black cursor-pointer">
                {showCurrentPassword ? (
                  <Eye
                    size={20}
                    onClick={() => setShowCurrentPassword(false)}
                  />
                ) : (
                  <EyeOff
                    size={20}
                    onClick={() => setShowCurrentPassword(true)}
                  />
                )}
              </span>
              <input
                type={showCurrentPassword ? "text" : "password"}
                className="w-full px-4 py-2 border-2 border-[#9061DB] bg-transparent rounded-md font-inter focus:outline-none focus:ring-2 focus:ring-[#9061DB] text-sm"
                placeholder="Enter your current password"
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  setErrors((prev) => ({ ...prev, currentPassword: "" }));
                }}
              />
            </div>
            {errors.currentPassword && (
              <p className="text-sm text-red-500 mt-1">
                {errors.currentPassword}
              </p>
            )}
          </div>

          {/* New Password */}
          <div className="mb-4">
            <label className="block text-sm mb-1 font-normal font-inter">
              New Password<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-black cursor-pointer">
                {showNewPassword ? (
                  <Eye size={20} onClick={() => setShowNewPassword(false)} />
                ) : (
                  <EyeOff size={20} onClick={() => setShowNewPassword(true)} />
                )}
              </span>
              <input
                type={showNewPassword ? "text" : "password"}
                className="w-full px-4 py-2 border-2 border-[#9061DB] bg-transparent rounded-md font-inter focus:outline-none focus:ring-2 focus:ring-[#9061DB] text-sm"
                placeholder="Enter your new password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setErrors((prev) => ({ ...prev, newPassword: "" }));
                }}
              />
            </div>
            {errors.newPassword && (
              <p className="text-sm text-red-500 mt-1">{errors.newPassword}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div className="mb-[30px]">
            <label className="block text-sm mb-1 font-normal font-inter">
              Confirm New Password<span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-black cursor-pointer">
                {showConfirmPassword ? (
                  <Eye
                    size={20}
                    onClick={() => setShowConfirmPassword(false)}
                  />
                ) : (
                  <EyeOff
                    size={20}
                    onClick={() => setShowConfirmPassword(true)}
                  />
                )}
              </span>
              <input
                type={showConfirmPassword ? "text" : "password"}
                className="w-full px-4 py-2 border-2 border-[#9061DB] text-sm bg-transparent rounded-md font-inter focus:outline-none focus:ring-2 focus:ring-[#9061DB]"
                placeholder="Confirm your new password"
                value={confirmNewPassword}
                onChange={(e) => {
                  setConfirmNewPassword(e.target.value);
                  setErrors((prev) => ({ ...prev, confirmNewPassword: "" }));
                }}
              />
            </div>
            {errors.confirmNewPassword && (
              <p className="text-sm text-red-500 mt-1">
                {errors.confirmNewPassword}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-between items-center">
            <span
              className="text-xs cursor-pointer sm:block hidden"
              onClick={() => navigate(-1)}
            >
              <span className="text-sm font-semibold text-[#9367D7]">Back</span>{" "}
            </span>
            <button
              type="submit"
              onClick={handleChangePassword}
              className="bg-primary text-sm font-semibold text-white px-6 py-2 rounded-md font-inter hover:scale-105 transition-transform duration-300 sm:w-auto"
              style={{ willChange: "transform" }}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-md max-w-sm w-full shadow-lg text-center">
            <p className="text-gray-800 mb-4">{modalMessage}</p>
            <button
                onClick={closeModal}
              className="px-4 py-2 bg-[#9061DB] text-white rounded hover:opacity-90"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangePassword;
